import type { GameSettings } from '../interfaces/game.interface';
import { PowerUpType } from '../interfaces/game.interface';
import { FOOD_DISTANCE_MAX, MIN_SNAKE_SEGMENTS_MAX } from './board.config';

/** Server simulation tick interval in milliseconds (20 ticks/sec). */
export const TICK_RATE_MS = 50;

export const GAME_DURATION_OPTIONS = [
  { value: 60, label: '1:00' },
  { value: 90, label: '1:30' },
  { value: 120, label: '2:00' },
  { value: 150, label: '2:30' },
  { value: 180, label: '3:00' },
] as const;

export type GameDuration = (typeof GAME_DURATION_OPTIONS)[number]['value'];

export const DEFAULT_GAME_DURATION: GameDuration = 150;

/** Interval between racing food spawns in milliseconds. */
export const RACING_FOOD_INTERVAL_MS = 15000;

/** How long racing food stays on the board before expiring (ms). */
export const RACING_FOOD_DURATION_MS = 3000;

/** Number of food items needed to earn 1 attack block. */
export const FOODS_PER_BLOCK = 3;

/** How long (ms) a pending attack block is highlighted before solidifying. */
export const BLOCK_WARNING_DURATION_MS = 2000;

/** Minimum ms between successive attack block fires for the same player. */
export const BLOCK_ATTACK_COOLDOWN_MS = 500;

/** Score deducted on any death (wall, self, or obstacle collision). Floor at 0. */
export const DEATH_SCORE_PENALTY = 10;

/**
 * How long (ms) a respawned snake ignores obstacles. Blocks are passed through
 * without being consumed; walls and self-collision still kill.
 */
export const SPAWN_PROTECTION_DURATION_MS = 1000;

/** Length (ms) of one on/off half-cycle of the spawn-protection blink. */
export const SPAWN_PROTECTION_BLINK_MS = 150;

/** Snake opacity during the off-phase of the spawn-protection blink (0–1). */
export const SPAWN_PROTECTION_DIM_ALPHA = 0.35;

/** Score awarded each time a player's snake grows past their personal max length. */
export const PEAK_LENGTH_BONUS = 0;

/** Score bonus for the player currently holding the Longest Snake title. Baked into the score directly. */
export const LONGEST_SNAKE_BONUS = 50;

/** Minimum snake length required before the Longest Snake title can be awarded. */
export const LONGEST_SNAKE_MIN_LENGTH = 20;

/** Minimum number of snake segments (spawn size and floor after death). */
export const MIN_SNAKE_SEGMENTS = 3;

/** Points awarded for eating regular food. */
export const REGULAR_FOOD_POINTS = 5;

/** Points awarded for eating racing food. */
export const RACING_FOOD_POINTS = 15;

/** Points awarded per block destroyed via the constrictor mechanic. */
export const BLOCK_DESTROY_POINTS = 15;

/** Minimum Manhattan distance for food spawn from snake head. */
export const FOOD_DISTANCE_MIN = 3;

// FOOD_DISTANCE_MAX is defined in board.config.ts (derived from BOARD_CONFIG.columns)
export { FOOD_DISTANCE_MAX } from './board.config';

/** Number of pre-generated distances in the spawn sequence. */
export const FOOD_DISTANCE_SEQUENCE_LENGTH = 15;

/** Initial snake speed in tiles per second (one tile every 125 ms at 50 ms tick). */
export const INITIAL_SNAKE_SPEED = 8;

/** Minimum snake speed in tiles per second (one tile every 200 ms). */
export const MIN_SNAKE_SPEED = 5;

/** Maximum snake speed in tiles per second (one tile every 50 ms tick). */
export const MAX_SNAKE_SPEED = 20;

// ---------------------------------------------------------------------------
// Power-up constants
// ---------------------------------------------------------------------------

/** Interval between power-up pickup spawns on a single board (ms). */
export const POWERUP_SPAWN_INTERVAL_MS = 20000;

/** How long a power-up pickup stays on the board before despawning (ms). */
export const POWERUP_LIFETIME_MS = 3500;

/** Maximum number of power-ups a player can hold (active + reserve). */
export const POWERUP_MAX_SLOTS = 2;

/** Maximum number of timed effects active on a player at once. */
export const POWERUP_MAX_EFFECTS = 2;

/** Tiles-per-second change applied per active speed effect stack. */
export const SPEED_DELTA_PER_STACK = 3;

/** Duration (ms) each power-up effect lasts once applied. */
export const POWERUP_DURATIONS: Record<PowerUpType, number> = {
  [PowerUpType.SPEED_UP]: 8000,
  [PowerUpType.SPEED_DOWN]: 8000,
  [PowerUpType.BULLDOZER]: 6000,
  [PowerUpType.FLASH]: 6000,
};

/** Duration (ms) the board stays visible during a Flash blink cycle (on-phase). */
export const FLASH_BLINK_ON_MS = 300;

/** Duration (ms) the board is blacked out during a Flash blink cycle (off-phase). */
export const FLASH_BLINK_OFF_MS = 200;

/** Opacity of the dark overlay during the Flash effect's off-phase (0–1). */
export const FLASH_OPACITY = 0.85;

// ---------------------------------------------------------------------------
// Sudden death constants
// ---------------------------------------------------------------------------

/** Duration of the freeze countdown before sudden death active play begins (ms). */
export const SUDDEN_DEATH_COUNTDOWN_MS = 5000;

/** Physical snake length (segments) at which participants respawn when sudden death starts. */
export const SUDDEN_DEATH_RESPAWN_SIZE = 5;

/** Physical snake length a participant must first reach to win sudden death. */
export const SUDDEN_DEATH_TARGET_SIZE = 20;

/** Interval between racing food spawns during sudden death (ms). */
export const SUDDEN_DEATH_RACING_FOOD_INTERVAL_MS = 5000;

/** Initial interval between server-spawned blocks during sudden death (ms). */
export const SUDDEN_DEATH_BLOCK_INITIAL_INTERVAL_MS = 3000;

/** Multiplier applied to the block interval after each spawn (< 1 = escalating). */
export const SUDDEN_DEATH_BLOCK_INTERVAL_DECAY = 0.9;

// ---------------------------------------------------------------------------
// Default game settings and limits
// ---------------------------------------------------------------------------

/** Single source of truth for default game settings. */
export const DEFAULT_GAME_SETTINGS: GameSettings = {
  initialSnakeSpeed: INITIAL_SNAKE_SPEED,
  minSnakeSpeed: MIN_SNAKE_SPEED,
  maxSnakeSpeed: MAX_SNAKE_SPEED,
  minSnakeSegments: MIN_SNAKE_SEGMENTS,
  regularFoodPoints: REGULAR_FOOD_POINTS,
  racingFoodPoints: RACING_FOOD_POINTS,
  racingFoodIntervalMs: RACING_FOOD_INTERVAL_MS,
  racingFoodDurationMs: RACING_FOOD_DURATION_MS,
  foodDistanceMin: FOOD_DISTANCE_MIN,
  foodDistanceMax: FOOD_DISTANCE_MAX,
  foodsPerBlock: FOODS_PER_BLOCK,
  blockWarningDurationMs: BLOCK_WARNING_DURATION_MS,
  blockAttackCooldownMs: BLOCK_ATTACK_COOLDOWN_MS,
  deathScorePenalty: DEATH_SCORE_PENALTY,
  peakLengthBonus: PEAK_LENGTH_BONUS,
  longestSnakeBonus: LONGEST_SNAKE_BONUS,
  longestSnakeMinLength: LONGEST_SNAKE_MIN_LENGTH,
  maxPlayers: 2,
  gameDuration: DEFAULT_GAME_DURATION,
  suddenDeathCountdownMs: SUDDEN_DEATH_COUNTDOWN_MS,
  suddenDeathRespawnSize: SUDDEN_DEATH_RESPAWN_SIZE,
  suddenDeathTargetSize: SUDDEN_DEATH_TARGET_SIZE,
  suddenDeathRacingFoodIntervalMs: SUDDEN_DEATH_RACING_FOOD_INTERVAL_MS,
  suddenDeathBlockInitialIntervalMs: SUDDEN_DEATH_BLOCK_INITIAL_INTERVAL_MS,
  suddenDeathBlockIntervalDecay: SUDDEN_DEATH_BLOCK_INTERVAL_DECAY,
  powerupSpawnIntervalMs: POWERUP_SPAWN_INTERVAL_MS,
  powerupLifetimeMs: POWERUP_LIFETIME_MS,
  powerupMaxEffects: POWERUP_MAX_EFFECTS,
  speedUpDurationMs: POWERUP_DURATIONS[PowerUpType.SPEED_UP],
  speedUpDelta: 3,
  speedDownDurationMs: POWERUP_DURATIONS[PowerUpType.SPEED_DOWN],
  speedDownDelta: SPEED_DELTA_PER_STACK,
  bulldozerDurationMs: POWERUP_DURATIONS[PowerUpType.BULLDOZER],
  flashDurationMs: POWERUP_DURATIONS[PowerUpType.FLASH],
};

/** Per-field min/max bounds used for both client-side validators and server-side clamping. */
export const GAME_SETTINGS_LIMITS: {
  [K in keyof GameSettings]: { min: number; max: number; step?: number };
} = {
  initialSnakeSpeed: { min: 6, max: 12, step: 1 },
  minSnakeSpeed: { min: 1, max: 20 },
  maxSnakeSpeed: { min: 5, max: 40 },
  minSnakeSegments: { min: 2, max: 8, step: 1 },
  regularFoodPoints: { min: 5, max: 100, step: 5 },
  racingFoodPoints: { min: 5, max: 200, step: 5 },
  racingFoodIntervalMs: { min: 5000, max: 30000, step: 5000 },
  racingFoodDurationMs: { min: 1500, max: 5000, step: 500 },
  foodDistanceMin: { min: 1, max: 10 },
  foodDistanceMax: { min: 2, max: 20 },
  foodsPerBlock: { min: 1, max: 7, step: 1 },
  blockWarningDurationMs: { min: 500, max: 5000, step: 500 },
  blockAttackCooldownMs: { min: 0, max: 3000, step: 500 },
  deathScorePenalty: { min: 0, max: 100, step: 5 },
  peakLengthBonus: { min: 0, max: 100, step: 5 },
  longestSnakeBonus: { min: 0, max: 100, step: 10 },
  longestSnakeMinLength: { min: 10, max: 30, step: 1 },
  maxPlayers: { min: 2, max: 4 },
  gameDuration: { min: 60, max: 180 },
  suddenDeathCountdownMs: { min: 3000, max: 10000 },
  suddenDeathRespawnSize: { min: 3, max: 15 },
  suddenDeathTargetSize: { min: 10, max: 50 },
  suddenDeathRacingFoodIntervalMs: { min: 1000, max: 30000 },
  suddenDeathBlockInitialIntervalMs: { min: 1000, max: 30000 },
  suddenDeathBlockIntervalDecay: { min: 0.5, max: 1.0 },
  powerupSpawnIntervalMs: { min: 5000, max: 60000, step: 5000 },
  powerupLifetimeMs: { min: 1500, max: 5000, step: 500 },
  powerupMaxEffects: { min: 1, max: 4, step: 1 },
  speedUpDurationMs: { min: 3000, max: 12000, step: 1000 },
  speedUpDelta: { min: 1, max: 5, step: 1 },
  speedDownDurationMs: { min: 3000, max: 12000, step: 1000 },
  speedDownDelta: { min: 1, max: 5, step: 1 },
  bulldozerDurationMs: { min: 3000, max: 15000, step: 1000 },
  flashDurationMs: { min: 2000, max: 15000, step: 1000 },
};
